/**
 * FleetFilters — Sprint 4.1 + Sprint 4.1.1.
 *
 * Painel ESQUERDA do Fleet Map.
 *   - Campo de busca textual (4.1.1) — device_id, customer, group, tag.name
 *   - Filtros (customer, group, tag, status, firmware, RSSI)
 *   - Stats compactos
 *   - Lista clicável de devices (Ctrl+click → multi-select; 4.1.1)
 *
 * Estado de filtros é do pai (MapView).
 */

function StatusDot({ status }) {
  const color = {
    online: "bg-noc-ok/10",
    weak: "bg-noc-warn/10",
    offline: "bg-noc-err/10",
  }[status] || "bg-noc-muted";
  return <span className={`inline-block w-2 h-2 rounded-full ${color}`} />;
}

function deviceStatus(d) {
  if (!d.online) return "offline";
  if (d.weak_signal) return "weak";
  return "online";
}

export default function FleetFilters({
  devices,
  stats,
  filters,
  setFilters,
  customers,
  groups,
  allTags,
  selectedIds,        // Set<string> — Sprint 4.1.1
  onToggleSelect,     // ({deviceId, ctrlKey}) — Sprint 4.1.1
  onSelectDevice,     // single-select (focar mapa)
  onResetFilters,
  onCollapse,         // M1 — recolher a sidebar (mostra botão flutuante)
  bare = false,       // Etapa 4.1 — render como seção (sem o shell <aside>),
                      // p/ compor dentro do menu lateral NOC.
}) {
  const handleField = (k, v) => setFilters({ ...filters, [k]: v });
  const Wrap = bare ? "div" : "aside";
  const wrapClass = bare
    ? "space-y-3 text-xs"
    : "ipwd-fleet-sidebar w-full md:w-[240px] flex-shrink-0 bg-noc-panel border border-noc-border rounded p-3 space-y-3 text-xs md:overflow-y-auto";

  const onlineCount = devices.filter((d) => d.online).length;
  const offlineCount = devices.length - onlineCount;
  const weakCount = devices.filter((d) => d.online && d.weak_signal).length;

  // Sprint 5.4 — instaladores distintos presentes na frota do mapa.
  const installerOptions = Array.from(
    new Set(devices.map((d) => d.installer).filter(Boolean))
  ).sort();

  return (
    <Wrap className={wrapClass}>
      <div className="flex items-center justify-between mb-1">
        <h2 className="ipwd-side-section-head">Filtros</h2>
        {onCollapse && (
          <button
            type="button"
            onClick={onCollapse}
            title="Recolher filtros"
            className="text-noc-muted hover:text-noc-text text-xs px-1.5 py-0.5 rounded border border-noc-border/60"
          >◀</button>
        )}
      </div>

      {/* Sprint 4.1.1: busca textual */}
      <label className="block">
        <span className="text-noc-muted">🔍 Buscar device</span>
        <input
          placeholder="device_id, cliente, grupo, tag…"
          value={filters.q || ""}
          onChange={(e) => handleField("q", e.target.value)}
          className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 mt-0.5"
        />
      </label>

      <div className="space-y-2">
        <label className="block">
          <span className="text-noc-muted">Cliente</span>
          <select
            value={filters.customer_id || ""}
            onChange={(e) => handleField("customer_id", e.target.value)}
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 mt-0.5"
          >
            <option value="">— todos —</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.code}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-noc-muted">Grupo</span>
          <select
            value={filters.group_id || ""}
            onChange={(e) => handleField("group_id", e.target.value)}
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 mt-0.5"
          >
            <option value="">— todos —</option>
            {groups
              .filter((g) =>
                !filters.customer_id || g.customer_id === filters.customer_id
              )
              .map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
          </select>
        </label>

        <div>
          <span className="text-noc-muted">Tags</span>
          <div className="flex flex-wrap gap-1 mt-0.5">
            {allTags.map((t) => {
              const active = (filters.tags || []).includes(t.name);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => {
                    const cur = new Set(filters.tags || []);
                    if (active) cur.delete(t.name); else cur.add(t.name);
                    handleField("tags", Array.from(cur));
                  }}
                  className={`px-2 py-0.5 rounded text-[10px] border ${
                    active
                      ? "border-noc-ok/30 bg-noc-ok/10 text-noc-ok"
                      : "border-noc-border text-noc-muted hover:text-noc-text"
                  }`}
                  style={
                    active
                      ? { borderColor: t.color, boxShadow: `0 0 0 1px ${t.color}` }
                      : {}
                  }
                >
                  ● {t.name}
                </button>
              );
            })}
            {allTags.length === 0 && (
              <span className="text-noc-muted/60">sem tags</span>
            )}
          </div>
        </div>

        <label className="block">
          <span className="text-noc-muted">Status</span>
          <select
            value={filters.status || ""}
            onChange={(e) => handleField("status", e.target.value)}
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 mt-0.5"
          >
            <option value="">— todos —</option>
            <option value="online">online</option>
            <option value="offline">offline</option>
            <option value="weak">sinal fraco</option>
            <option value="relay_off">relé desligado</option>
            <option value="ota_error">OTA falhou</option>
          </select>
        </label>

        {/* Sprint 5.4 — Mapa Avançado: saúde, estoque, instalador */}
        <label className="block">
          <span className="text-noc-muted">Saúde</span>
          <select
            value={filters.health_status || ""}
            onChange={(e) => handleField("health_status", e.target.value)}
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 mt-0.5"
          >
            <option value="">— todas —</option>
            <option value="healthy">saudável</option>
            <option value="warning">atenção</option>
            <option value="critical">crítico</option>
            <option value="offline">offline</option>
          </select>
        </label>

        <label className="block">
          <span className="text-noc-muted">Estoque</span>
          <select
            value={filters.inventory_status || ""}
            onChange={(e) => handleField("inventory_status", e.target.value)}
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 mt-0.5"
          >
            <option value="">— todos —</option>
            <option value="stock">em estoque</option>
            <option value="reserved">reservado</option>
            <option value="installed">instalado</option>
            <option value="maintenance">manutenção</option>
            <option value="lost">perdido</option>
          </select>
        </label>

        {installerOptions.length > 0 && (
          <label className="block">
            <span className="text-noc-muted">Instalador</span>
            <select
              value={filters.installer || ""}
              onChange={(e) => handleField("installer", e.target.value)}
              className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 mt-0.5"
            >
              <option value="">— todos —</option>
              {installerOptions.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </label>
        )}

        <label className="block">
          <span className="text-noc-muted">Firmware</span>
          <input
            placeholder="ex: 2.5.1"
            value={filters.fw_version || ""}
            onChange={(e) => handleField("fw_version", e.target.value)}
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 mt-0.5"
          />
        </label>

        <label className="block">
          <span className="text-noc-muted">RSSI mín (dBm)</span>
          <input
            type="number"
            placeholder="-90 a -30"
            value={filters.rssi_min ?? ""}
            onChange={(e) => handleField(
              "rssi_min",
              e.target.value === "" ? null : Number(e.target.value)
            )}
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 mt-0.5"
          />
        </label>

        <button
          type="button"
          onClick={onResetFilters}
          className="w-full text-[10px] px-2 py-1 rounded border border-noc-border text-noc-muted hover:text-noc-text"
        >
          Limpar filtros
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-1 pt-2 border-t border-noc-border/40">
        <div className="bg-noc-ok/10 border border-noc-ok/30 rounded p-1 text-center">
          <div className="text-noc-ok font-mono text-sm">{onlineCount}</div>
          <div className="text-[9px] text-noc-muted uppercase">online</div>
        </div>
        <div className="bg-noc-warn/10 border border-noc-warn/30 rounded p-1 text-center">
          <div className="text-noc-warn font-mono text-sm">{weakCount}</div>
          <div className="text-[9px] text-noc-muted uppercase">weak</div>
        </div>
        <div className="bg-noc-err/10 border border-noc-err/30 rounded p-1 text-center">
          <div className="text-noc-err font-mono text-sm">{offlineCount}</div>
          <div className="text-[9px] text-noc-muted uppercase">offline</div>
        </div>
      </div>

      {stats && (
        <div className="text-[10px] text-noc-muted/80 space-y-0.5">
          <div>com localização: <span className="text-noc-text font-mono">{stats.with_location}/{stats.total}</span></div>
        </div>
      )}

      {/* Lista de devices */}
      <div className="pt-2 border-t border-noc-border/40">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-xs font-semibold">Devices ({devices.length})</h3>
          {selectedIds && selectedIds.size > 0 && (
            <div className="flex items-center gap-1">
              <span className="text-[9px] text-noc-ok">{selectedIds.size} sel.</span>
              <button
                type="button"
                onClick={() => setFilters({ ...filters, only_selected: !filters.only_selected })}
                title={filters.only_selected ? "Mostrar todos" : "Mostrar só selecionados"}
                className={`text-[9px] px-1.5 py-0.5 rounded border ${
                  filters.only_selected
                    ? "border-noc-accent/30 text-noc-accent bg-noc-accent/10"
                    : "border-noc-border text-noc-muted hover:text-noc-text"
                }`}
              >
                {filters.only_selected ? "✓ filtrado" : "filtrar"}
              </button>
            </div>
          )}
        </div>
        <div className="text-[9px] text-noc-muted/60 mb-1">
          click: focar · Ctrl/⌘+click: selecionar
        </div>
        <div className="max-h-72 overflow-y-auto space-y-0.5">
          {devices.map((d) => {
            const status = deviceStatus(d);
            const isSelected = selectedIds && selectedIds.has(d.device_id);
            return (
              <button
                key={d.device_id}
                type="button"
                onClick={(e) => {
                  if (e.ctrlKey || e.metaKey) {
                    if (onToggleSelect) onToggleSelect(d.device_id);
                  } else {
                    if (onSelectDevice) onSelectDevice(d.device_id);
                  }
                }}
                className={`w-full text-left px-2 py-1 rounded flex items-center gap-2 ${
                  isSelected
                    ? "bg-noc-ok/10 border border-noc-ok/30"
                    : "hover:bg-noc-bg/50 border border-transparent"
                }`}
              >
                <StatusDot status={status} />
                <span className="flex-1 font-mono text-[11px] truncate">
                  {d.device_id}
                </span>
                {d.ota_state === "failed" && (
                  <span className="text-[9px] text-noc-err" title="OTA falhou">OTA✗</span>
                )}
                {d.rssi != null && (
                  <span className="text-[9px] text-noc-muted font-mono">{d.rssi}</span>
                )}
                {!d.lat && (
                  <span className="text-[9px] text-noc-warn" title="sem geo">📍?</span>
                )}
              </button>
            );
          })}
          {devices.length === 0 && (
            <div className="text-noc-muted py-2 text-center">
              Nenhum device com os filtros atuais.
            </div>
          )}
        </div>
      </div>
    </Wrap>
  );
}
